import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import dayjs from "dayjs";
import Cookie from "js-cookie";
import useCart from "store";
import Api from "utils/wApi/index";
import { fDate } from "utils/formatTime";
import { getItemType } from "utils/common";
import { SET_MENU_TYPE } from "utils/function";
import {
	FIRST_ST_TO_UPPER,
	SET_PRICE_CURRENCY,
	ASC_SORT,
} from "utils/function";

// 장바구니 담기 함수
import {
	addNormal,
	addNormalOptions,
	addTime,
	addTimeOption,
} from "utils/addCart";

import Cart from "layout/cart";
import {
	Container,
	Typography,
	Box,
	Stack,
	Button,
	Select,
	Snackbar,
} from "@mui/material";
import {
	CommonCounter,
	CommonModal,
	SvgIconStyle,
} from "components";
import {
	ItemCard,
	GoListButton,
	AddCartButton,
	FastestButton,
	Loading,
} from "components/unitComponent";
import SelectTime from "components/SelectTime";

// asset
import DefaultThumb from "assets/image/default_thumb.png";
import SearchIcon from "assets/icons/search.svg";

export default function Detail() {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { id: MENU_ID } = useParams();

	const { language, timezone, cart, removeAllCart, removeCart, callCart } =
		useCart((state) => state);

	const [loading, setLoading] = useState(true); // 상품 로딩
	const [menu, setMenu] = useState(null); // 상품 상세정보
	const [menuType, setMenuType] = useState("NORMAL"); // 상품 종류

	const [open, setOpen] = useState(false); // 상세정보 보기 모달 온오프
	const [thumbModal, setThumbModal] = useState(false); // 썸네일 보기 모달
	const [snackbarState, setSnackbarState] = useState({
		open: false,
		text: "",
		time: 1000,
	}); // 토스트알림 온오프

	const [count, setCount] = useState(1); //구매수량
	const [time, setTime] = useState(null); //선택된 시간

	// [timeOption] :: 시간상품 종류형 옵션(id 값으로 저장)
	const [timeOption, setTimeOption] = useState(null);
	// [normalOption] :: 일반 옵션(id 값으로 저장)
	const [normalOption, setNormalOption] = useState(null);

	// ----- 필수/선택 옵션 카테고리 -----
	// 선택한 카테고리 {아이디, 카테고리명, 옵션 리스트}[]
	const [categoryArr, setCategoryArr] = useState([
		{
			id: 0,
			name: "",
			optionArr: [],
		},
	]);
	// 선택한 옵션 배열
	const [categoryOption, setCategoryOption] = useState([]);
	// 선택한 옵션의 남은 재고
	const [maxOptionQty, setMaxOptionQty] = useState(0);

	// ===== CLICK EVENT =====
	// ===== 필수/선택 옵션 카테고리 클릭 이벤트 =====
	const handleCategoryOption = (category, option, index) => {
		// 선택한 갯수 초기화
		setCount(1);

		// 동일 카테고리 선택유무
		const isSameDepth = !!categoryOption.find(
			(x) => x.parentOptionId === option.parentOptionId
		);

		/*
		 * 카테고리 변경
		 * 하위 카테고리가 있을 때만 업데이트
		 */
		if (option?.topOptions !== null) {
			// 순서 변경한 옵션 리스트
			const changeOption = ASC_SORT(
				option?.topOptions?.[0]?.optionStocks,
				"orderDescending",
				""
			);

			// 동일 카테고리 선택시
			let newCategoryArr = [];
			if (isSameDepth && categoryArr.length > 1) {
				categoryArr.splice(index, categoryArr.length - index);

				const sameOption = ASC_SORT(category.optionArr, "orderDescending", "");
				newCategoryArr = [
					...categoryArr,
					{
						id: category.id,
						name: category.name,
						optionArr: sameOption,
					},
					{
						id: option.topOptions[0].idTopOption,
						name: option.topOptions[0].topName,
						optionArr: changeOption,
					},
				];
			} else {
				newCategoryArr = [
					...categoryArr,
					{
						id: option.topOptions[0].idTopOption,
						name: option.topOptions[0].topName,
						optionArr: changeOption,
					},
				];
			}

			setCategoryArr(() => newCategoryArr);
		}

		/*
		 * 옵션 변경
		 */
		if (isSameDepth) {
			categoryOption.splice(index, categoryOption.length - index);
		}
		setCategoryOption(() => [...categoryOption, option]);

		// 상품 최대수량 계산
		if (menuType === "NORMAL_TOP") {
			if (option.topOptions === null) {
				setMaxOptionQty(option?.optionStockQty);
			} else {
				setMaxOptionQty(0);
			}
		}
		getLimit();
	};
	// ===== 필수/선택 옵션 카테고리 클릭 이벤트 =====

	// ===== 장바구니 담기 =====
	const addCart = async () => {
		switch (menuType) {
			// 일반 상품
			case "NORMAL":
				await addNormal({
					id: menu.idMenu,
					buyCount: count,
					price: calcTotalPrice(),
				})
					.then(() => {
						setSnackbarState({
							open: true,
							text: t("added-cart"),
							time: 1000,
						});
					})
					.catch((error) => {
						setSnackbarState({ open: true, text: error.message, time: 3000 });
					});
				setTimeout(() => {
					callCart();
				}, 300);

				break;
			// 필수,선택 옵선 상품
			case "NORMAL_TOP":
			case "NORMAL_SELECT":
				await addNormalOptions({
					id: menu.idMenu,
					buyCount: count,
					price: calcTotalPrice(),
					optionId: categoryOption,
					optionMode: menuType === "NORMAL_TOP" ? "TOP" : "SELECT",
				})
					.then(() => {
						setSnackbarState({
							open: true,
							text: t("added-cart"),
							time: 1000,
						});
					})
					.catch((error) => {
						setSnackbarState({ open: true, text: error.message, time: 3000 });
					});
				setTimeout(() => {
					callCart();
				}, 300);
				break;
			// 시간상품
			case "TIME":
				await addTime({
					id: time.idMenuTime,
					buyCount: count,
					price: calcTotalPrice(),
				})
					.then(() => {
						setSnackbarState({
							open: true,
							text: t("added-cart"),
							time: 1000,
						});
					})
					.catch((error) => {
						setSnackbarState({ open: true, text: error.message, time: 3000 });
					});
				setTimeout(() => {
					callCart();
				}, 300);
				break;
			case "TIME_OPTION":
				await addTimeOption({
					id: time.idMenuTime,
					buyCount: count,
					price: calcTotalPrice(),
					optionId: timeOption.id,
				})
					.then(() => {
						setSnackbarState({
							open: true,
							text: t("added-cart"),
							time: 1000,
						});
					})
					.catch((error) => {
						setSnackbarState({ open: true, text: error.message, time: 3000 });
					});
				setTimeout(() => {
					callCart();
				}, 300);
				break;

			default:
				alert("담을 수 있는 상품이 없습니다.");
				break;
		}

		// 초기화
		setCount(1);
		setTimeOption(null);
		setNormalOption(null);
		setCategoryOption([]);

		INIT();
	};
	// ===== 장바구니 담기 =====
	// ===== CLICK EVENT =====

	/*
	 * 상품 최대 수량 계산
	 * salesLimitOnce : 회당판매한도
	 */
	const getLimit = () => {
		let max = 0;

		// 시간 상품일 경우
		if (menuType.includes("TIME")) {
			max = menu?.salesLimitOnce
				? Math.min(...[time?.stock, menu?.salesLimitOnce])
				: time?.stock;
		} else if (menuType === "NORMAL_TOP") {
			// 필수 옵션 상품일 경우
			max = menu?.salesLimitOnce
				? Math.min(...[menu?.salesLimitOnce, maxOptionQty])
				: maxOptionQty;
		} else {
			max = menu?.salesLimitOnce
				? Math.min(...[menu?.normalInfo?.stock, menu?.salesLimitOnce])
				: menu?.normalInfo?.stock;
		}

		return max;
	};
	// 시간 선택시 최대 수량 조정
	const setMaxCount = (maxCount) => {
		maxCount < count ? setCount(maxCount) : null;
	};

	/*
	 * 상품금액 계산
	 */
	const calcTotalPrice = () => {
		let total = 0;

		switch (menuType) {
			// 필수옵션, 선택옵션 상품
			case "NORMAL_TOP":
			case "NORMAL_SELECT": {
				const option = categoryOption.reduce(
					(acc, item) => acc + item.addPrice.krw,
					0
				);
				total = (menu.price.krw + option) * count;
				break;
			}
			// 시간종류형 상품
			case "TIME_OPTION": {
				total = (menu.price.krw + timeOption?.addPrice?.krw || 0) * count;
				break;
			}
			// 시간상품, , 일반상품
			default: {
				total = menu.price.krw * count;
			}
		}

		return total;
	};

	// ===== 상품 데이터 확인 =====
	const INIT = async () => {
		try {
			// 상품 상세정보 호출
			const {
				data: { data },
			} = await Api.getProductDetail(null, MENU_ID, {
				params: {
					date: Cookie.get("qs_kiosk_date") ?? dayjs().format("YYYY-MM-DD"),
					timezone: timezone,
				},
			});
			const MENU_TYPE = SET_MENU_TYPE(data);
			setMenu(data);
			setMenuType(MENU_TYPE);

			// ----- 선택/필수 옵션 카테고리 데이터 세팅 -----
			/*
			 * 필수 : 3depth까지 가능
			 * 선택 : 1depth만 가능
			 */
			if (MENU_TYPE === "NORMAL_TOP" || MENU_TYPE === "NORMAL_SELECT") {
				const changeCategory = ASC_SORT(
					data?.normalInfo?.topOptions?.[0]?.optionStocks,
					"orderDescending",
					""
				);

				// 4. 초기값 설정
				setCategoryArr([
					{
						id: data?.normalInfo?.topOptions?.[0].idTopOption,
						name: data?.normalInfo?.topOptions?.[0].topName,
						optionArr: changeCategory,
					},
				]);
				// 5. 옵션은 초기화
				setCategoryOption([]);
			}
			// ----- 선택/필수 옵션 카테고리 데이터 세팅 -----
		} catch (error) {
			alert(error.message);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		INIT();
	}, []);

	// 재고 폴링 (5초마다 상품 데이터 갱신)
	useEffect(() => {
		if (loading) return;

		const POLLING_INTERVAL = 5000;

		const poll = async () => {
			if (document.hidden) return;
			try {
				const {
					data: { data },
				} = await Api.getProductDetail(null, MENU_ID, {
					params: {
						date: Cookie.get("qs_kiosk_date") ?? dayjs().format("YYYY-MM-DD"),
						timezone: timezone,
					},
				});
				setMenu(data);

				// 옵션 재고 갱신 (categoryArr 내 옵션 객체의 품절/재고 업데이트)
				const topOptionStocks = data?.normalInfo?.topOptions?.[0]?.optionStocks;
				if (topOptionStocks) {
					const stockMap = {};
					const buildStockMap = (optionStocks) => {
						if (!optionStocks) return;
						optionStocks.forEach((opt) => {
							stockMap[opt.optionId] = opt;
							opt.topOptions?.forEach((top) => buildStockMap(top.optionStocks));
						});
					};
					buildStockMap(topOptionStocks);

					setCategoryArr((prev) => {
						prev.forEach((cat) => {
							cat.optionArr?.forEach((opt) => {
								const updated = stockMap[opt.optionId];
								if (updated) {
									opt.soldOut = updated.soldOut;
									opt.soldOutChk = updated.soldOutChk;
									opt.optionStockQty = updated.optionStockQty;
								}
							});
						});
						return [...prev];
					});
				}
			} catch (e) {
				// 폴링 실패 시 기존 데이터 유지
			}
		};

		const intervalId = setInterval(poll, POLLING_INTERVAL);
		return () => clearInterval(intervalId);
	}, [loading, MENU_ID, timezone]);

	return (
		<Cart noCategory>
			{loading ? (
				<Loading />
			) : (
				<>
					<Stack gap={"40px"} sx={{ pt: 4.5, px: 7 }}>
						{/* 상품 정보 */}
						<Stack direction={"row"} gap={"30px"}>
							<Box
								sx={{
									position: "relative",
									borderRadius: "4px",
									bgcolor: "grey.400",
									overflow: "hidden",
									minWidth: 180,
									height: 180,
								}}
								onClick={() => setThumbModal(true)}
							>
								{/* 품절 */}
								{menu?.soldOut ||
								menu?.normalInfo?.soldOutChk ||
								menu?.timeInfo?.soldOutChk ? (
									<Box
										key={menu.idMenu}
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											width: "100%",
											height: "100%",
											bgcolor: "#00000096",
											position: "absolute",
										}}
									>
										<Typography variant="h2" color={"white"}>
											SOLD OUT
										</Typography>
									</Box>
								) : null}
								{/* 돋보기 아이콘 */}
								<Box
									sx={{
										width: 36,
										height: 36,
										borderRadius: 100,
										bgcolor: "#00000080",
										position: "absolute",
										top: 8,
										left: 8,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<SvgIconStyle
										src={SearchIcon}
										size={20}
										sx={{ color: "white" }}
									/>
								</Box>
								<Box
									sx={{
										width: "100%",
										height: "100%",
										background: `center/cover no-repeat url(${
											menu?.thumbnailImage ? menu?.thumbnailImage : DefaultThumb
										})`,
									}}
								/>
							</Box>

							<Stack
								sx={{
									py: 1.25,
									justifyContent: "space-between",
								}}
							>
								<Stack gap={1.25}>
									<Typography
										variant="h4"
										sx={{
											textOverflow: "ellipsis",
											overflow: "hidden",
											WebkitLineClamp: 2,
											WebkitBoxOrient: "vertical",
											display: "-webkit-box",
										}}
									>
										{menu.menuName[language]}
									</Typography>
									<Typography
										variant="subtitle2"
										color={"grey.200"}
										fontSize={20}
										sx={{
											textOverflow: "ellipsis",
											overflow: "hidden",
											WebkitLineClamp: 1,
											WebkitBoxOrient: "vertical",
											display: "-webkit-box",
										}}
									>
										{menu.menuSubTitle[language]}
									</Typography>

									<Typography
										variant="body2"
										color="grey.100"
										sx={{ textDecoration: "underline" }}
										onClick={() => setOpen(true)}
									>
										{t("detail-info")}
									</Typography>
								</Stack>
								<Typography variant="h4" color="primary.main">
									{menu?.qty === 0
										? "품절"
										: SET_PRICE_CURRENCY(calcTotalPrice(), language)}
								</Typography>
							</Stack>
						</Stack>
						{/* 상품 정보 */}

						{/* ===== 옵션, 수량 선택 ===== */}
						<Stack gap={"20px"}>
							{/* ===== 필수/선택 옵션 ===== */}
							{(menuType === "NORMAL_TOP" || menuType === "NORMAL_SELECT") && (
								<>
									<Typography variant="h5">
										{t("select-option-count")}
									</Typography>

									<Typography variant="subtitle2" color={"grey.100"}>
										{t("select-option")}
									</Typography>
									{categoryArr.length > 0 && (
										<Box>
											{categoryArr.map((category, index) => (
												<>
													{/* ----- 카테고리 명 ----- */}
													<Typography
														key={category?.id}
														variant="subtitle2"
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 0.2,
															mb: 1.2,
														}}
													>
														{category?.name?.[language]}

														{/* 카테고리 필수 | 선택 */}
														{menuType === "NORMAL_TOP" ? (
															<Typography
																variant="subtitle2"
																color="primary.main"
															>
																{/* (필수) */}
																{`(${t("necessary")})`}
															</Typography>
														) : (
															<Typography variant="subtitle1" color="grey.200">
																{/* (선택) */}
																{`(${t("select")})`}
															</Typography>
														)}
													</Typography>
													{/* ----- 카테고리 명 ----- */}

													{/* ===== 옵션 버튼 ===== */}
													<Stack
														sx={{
															flexWrap: "wrap",
														}}
														direction={"row"}
														gap={"20px"}
														// alignItems={"center"}
														mb={1}
													>
														{category?.optionArr?.map((option, idx) => {
															// 품절 여부
															const SOLDOUT =
																option.soldOut ||
																option.soldOutChk ||
																option.optionStockQty === 0
																	? true
																	: false;

															// 선택된 옵션 여부
															const SELECTED =
																categoryOption.length > 0 &&
																categoryOption?.includes(option);

															return (
																<Button
																	key={option.optionId}
																	disabled={SOLDOUT}
																	variant={
																		SELECTED || SOLDOUT
																			? "contained"
																			: "outlined"
																	}
																	sx={{
																		flexDirection: "column",
																		minWidth: 0,
																		maxWidth: "150px",
																		width: "150px",
																		color: "black",
																		borderColor: "grey.100",
																	}}
																	onClick={() =>
																		handleCategoryOption(
																			category,
																			option,
																			index
																		)
																	}
																>
																	<Typography
																		color={
																			SELECTED || SOLDOUT ? "white" : "black"
																		}
																		sx={{
																			width: "100%",
																			minWidth: 0,
																			whiteSpace: "normal",
																			wordBreak: "normal",
																			overflowWrap: "anywhere",
																		}}
																	>
																		{option?.name[language]}
																	</Typography>

																	<Typography
																		color={
																			SELECTED || SOLDOUT ? "white" : "black"
																		}
																		sx={{ wordBreak: "break-all" }}
																	>
																		{SOLDOUT
																			? t("sold-out")
																			: `${
																					option?.addPrice?.krw >= 0 ? "+" : "-"
																			  } ${SET_PRICE_CURRENCY(
																					option?.addPrice?.krw,
																					language
																			  )}`}
																	</Typography>
																</Button>
															);
														})}
													</Stack>
													{/* ===== 옵션 버튼 ===== */}
												</>
											))}
										</Box>
									)}
								</>
							)}
							{/* ===== 필수/선택 옵션 ===== */}

							{/* ===== 시간 상품 ===== */}
							{menuType.includes("TIME") && (
								<>
									<Typography variant="h5">{t("select-pickup")}</Typography>

									<SelectTime
										menu={menu}
										time={time}
										timeOption={timeOption}
										setTime={setTime}
										setTimeOption={setTimeOption}
										setCount={setCount}
										setMaxCount={setMaxCount}
									/>
								</>
							)}
							{/* ===== 시간 상품 ===== */}

							{/* ===== 일반 상품 ===== */}
							{menuType === "NORMAL" && (
								<Typography variant="h5">{t("select-count")}</Typography>
							)}
							{/* ===== 일반 상품 ===== */}

							{/* ===== 상품 수량을 선택해주세요 ===== */}
							<Typography variant="subtitle2" color={"grey.100"}>
								{t("select-count")}
							</Typography>

							<Stack
								direction={"row"}
								alignItems={"center"}
								justifyContent={"space-between"}
							>
								<CommonCounter
									count={count}
									setCount={setCount}
									item={menu}
									max={getLimit()}
								/>

								<Typography variant="h4" color="primary.main">
									{menu?.qty === 0
										? "품절"
										: SET_PRICE_CURRENCY(calcTotalPrice(), language)}
								</Typography>
							</Stack>
						</Stack>
						{/* ===== 옵션, 수량 선택 ===== */}

						{/* 필수값 미선택 안내문구 */}
						{menuType === "NORMAL_TOP" &&
						(categoryOption.length === 0 ||
							categoryOption[categoryOption.length - 1].topOptions !== null) ? (
							<Typography variant="body2" color="primary.main">
								{t("select-option")}
							</Typography>
						) : null}
						{/* 장바구니 담기 버튼 */}
						<AddCartButton
							sx={{ my: 4 }}
							onClick={() => addCart()}
							disabled={
								menu?.soldOut ||
								menu?.normalInfo?.soldOutChk ||
								menu?.timeInfo?.soldOutChk ||
								(menuType === "NORMAL_TOP" &&
									(categoryOption.length === 0 ||
										categoryOption[categoryOption.length - 1].topOptions !==
											null)) ||
								(menuType.includes("TIME") && (!time || time?.stock === 0))
							}
							isSoldOut={
								menu?.soldOut ||
								menu?.normalInfo?.soldOutChk ||
								menu?.timeInfo?.soldOutChk
							}
							requireOption={
								(menuType === "NORMAL_TOP" &&
									(categoryOption.length === 0 ||
										categoryOption[categoryOption.length - 1].topOptions !==
											null)) ||
								(menuType.includes("TIME") && (!time || time?.stock === 0))
							}
							t={t}
						/>

						{/* 안내 문구 */}
						<Stack
							sx={{
								borderTop: "1px solid #e0e0e0",
								pt: 5,
								pb: 30,
								textAlign: "center",
							}}
						>
							<Typography
								variant="body2"
								color={"grey.100"}
								whiteSpace={"pre-line"}
							>
								{t("menu-info")}
							</Typography>
						</Stack>
						{/* 안내 문구 */}

						{/* toast */}
						<Snackbar
							anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
							open={snackbarState.open}
							message={snackbarState.text}
							autoHideDuration={snackbarState.time}
							onClose={() =>
								setSnackbarState({ open: false, text: "", time: 1000 })
							}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
							}}
						/>
						{/* toast */}
					</Stack>
				</>
			)}

			{/* 썸네일 크게 보기 모달 */}
			<CommonModal
				className="thumb-modal"
				open={thumbModal}
				width="90vw"
				onClose={() => setThumbModal(false)}
				footer={{
					label: t("close"),
					onClick: () => setThumbModal(false),
				}}
			>
				<Box>
					<img
						src={menu?.thumbnailImage ? menu?.thumbnailImage : DefaultThumb}
						style={{
							width: "100%",
							height: "100%",
							objectFit: "contain",
							borderRadius: "8px",
						}}
					/>
				</Box>
			</CommonModal>

			{/* 상품 상세정보 보기 모달 */}
			<CommonModal
				className="menu-detail-modal"
				open={open}
				width="90vw"
				onClose={() => {}}
				footer={{
					label: t("close"),
					sx: { fontWeight: 700, letterSpacing: -1 },
					onClick: () => setOpen(false),
				}}
				sx={{ minWidth: 400 }}
			>
				<Box sx={{ maxHeight: "75vh", overflowY: "scroll" }}>
					<Box
						dangerouslySetInnerHTML={{
							__html: menu?.information,
						}}
					/>
				</Box>
			</CommonModal>
		</Cart>
	);
}
